import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { 
  Calendar, 
  Clock,
  CheckCircle2,
  AlertTriangle,
  PlayCircle,
  Circle,
  MessageSquare
} from 'lucide-react';
import { checklistInstancesApi, ChecklistItemInstance } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { LoadingSpinner } from '../components/LoadingScreen';
import { useTelegram } from '../hooks/useTelegram';
import { ChecklistInstanceModal } from '../components/ChecklistInstanceModal';
import { isChecklistInstanceOverdue } from '../utils/overdue';

type Horizon = 'today' | 'tomorrow' | 'week' | 'month';

export function ChecklistPlanPage() {
  const { hapticFeedback, showAlert } = useTelegram();
  const [horizon, setHorizon] = useState<Horizon>('today');
  const [selectedInstance, setSelectedInstance] = useState<ChecklistItemInstance | null>(null);

  const { data: planData, isLoading } = useQuery({
    queryKey: ['checklist-plan', horizon],
    queryFn: () => checklistInstancesApi.getPlan(horizon),
  });

  const handleStatusChange = async (instanceId: string, status: 'IN_PROGRESS' | 'COMPLETED') => {
    try {
      hapticFeedback('medium');
      await checklistInstancesApi.updateStatus(instanceId, status);
      hapticFeedback('success');
    } catch (error: any) {
      hapticFeedback('error');
      showAlert(error.message || 'Не удалось обновить статус');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const statusConfig = {
    ASSIGNED: { label: 'Назначена', icon: Circle, color: 'text-gray-500', badge: 'default' as const },
    IN_PROGRESS: { label: 'В работе', icon: PlayCircle, color: 'text-blue-500', badge: 'info' as const },
    COMPLETED: { label: 'Выполнена', icon: CheckCircle2, color: 'text-green-500', badge: 'success' as const },
    COMPLETED_PENDING: { label: 'Ожидает приёмки', icon: Clock, color: 'text-yellow-500', badge: 'warning' as const },
    ACCEPTED: { label: 'Принята', icon: CheckCircle2, color: 'text-green-500', badge: 'success' as const },
    REJECTED: { label: 'Отклонена', icon: AlertTriangle, color: 'text-red-500', badge: 'danger' as const },
  };

  const isOverdue = (instance: ChecklistItemInstance): boolean => isChecklistInstanceOverdue(instance);

  return (
    <div className="p-4 space-y-4">
      {/* Заголовок */}
      <div>
        <h1 className="text-2xl font-bold text-tg-text flex items-center gap-2">
          <Calendar className="w-6 h-6" />
          План
        </h1>
      </div>

      {/* Горизонты */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {(['today', 'tomorrow', 'week', 'month'] as Horizon[]).map((h) => (
          <Button
            key={h}
            variant={horizon === h ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => {
              setHorizon(h);
              hapticFeedback('light');
            }}
          >
            {h === 'today' && 'На сегодня'}
            {h === 'tomorrow' && 'На завтра'}
            {h === 'week' && 'На этой неделе'}
            {h === 'month' && 'В этом месяце'}
          </Button>
        ))}
      </div>

      {/* Группировка */}
      {planData && (
        <div className="space-y-4">
          {/* Регламентные */}
          {planData.grouped.regulation.length > 0 && (
            <div>
              <h2 className="text-sm font-medium text-tg-hint mb-2">Регламентные</h2>
              <div className="space-y-2">
                {planData.grouped.regulation.map((instance) => {
                  const config = statusConfig[instance.status];
                  const overdue = isOverdue(instance);
                  return (
                    <Card 
                      key={instance.id} 
                      className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => setSelectedInstance(instance)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h3 className="font-semibold text-tg-text">{instance.rule?.text}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant={config.badge}>{config.label}</Badge>
                            {overdue && <Badge variant="danger">Просрочено</Badge>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 mt-3 text-sm text-tg-hint">
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>{format(new Date(instance.dueAt), 'dd.MM.yyyy HH:mm', { locale: ru })}</span>
                        </div>
                      </div>
                      {instance.status === 'ASSIGNED' && (
                        <div className="flex gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleStatusChange(instance.id, 'IN_PROGRESS')}
                          >
                            <PlayCircle className="w-4 h-4 mr-1" />
                            В процессе
                          </Button>
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => handleStatusChange(instance.id, 'COMPLETED')}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Выполнено
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSelectedInstance(instance)}
                          >
                            <MessageSquare className="w-4 h-4 mr-1" />
                            Комментарий
                          </Button>
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {/* Мероприятия */}
          {planData.grouped.event.length > 0 && (
            <div>
              <h2 className="text-sm font-medium text-tg-hint mb-2">Мероприятия</h2>
              <div className="space-y-2">
                {planData.grouped.event.map((instance) => {
                  const config = statusConfig[instance.status];
                  const overdue = isOverdue(instance);
                  return (
                    <Card 
                      key={instance.id} 
                      className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => setSelectedInstance(instance)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h3 className="font-semibold text-tg-text">{instance.rule?.text}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant={config.badge}>{config.label}</Badge>
                            {overdue && <Badge variant="danger">Просрочено</Badge>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 mt-3 text-sm text-tg-hint">
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>{format(new Date(instance.dueAt), 'dd.MM.yyyy HH:mm', { locale: ru })}</span>
                        </div>
                      </div>
                      {instance.status === 'ASSIGNED' && (
                        <div className="flex gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleStatusChange(instance.id, 'IN_PROGRESS')}
                          >
                            <PlayCircle className="w-4 h-4 mr-1" />
                            В процессе
                          </Button>
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => handleStatusChange(instance.id, 'COMPLETED')}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Выполнено
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSelectedInstance(instance)}
                          >
                            <MessageSquare className="w-4 h-4 mr-1" />
                            Комментарий
                          </Button>
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {/* Личные */}
          {planData.grouped.personal.length > 0 && (
            <div>
              <h2 className="text-sm font-medium text-tg-hint mb-2">Личные</h2>
              <div className="space-y-2">
                {planData.grouped.personal.map((instance) => {
                  const config = statusConfig[instance.status];
                  const overdue = isOverdue(instance);
                  return (
                    <Card 
                      key={instance.id} 
                      className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => setSelectedInstance(instance)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h3 className="font-semibold text-tg-text">{instance.rule?.text}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant={config.badge}>{config.label}</Badge>
                            {overdue && <Badge variant="danger">Просрочено</Badge>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 mt-3 text-sm text-tg-hint">
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>{format(new Date(instance.dueAt), 'dd.MM.yyyy HH:mm', { locale: ru })}</span>
                        </div>
                      </div>
                      {instance.status === 'ASSIGNED' && (
                        <div className="flex gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleStatusChange(instance.id, 'IN_PROGRESS')}
                          >
                            <PlayCircle className="w-4 h-4 mr-1" />
                            В процессе
                          </Button>
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => handleStatusChange(instance.id, 'COMPLETED')}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Выполнено
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSelectedInstance(instance)}
                          >
                            <MessageSquare className="w-4 h-4 mr-1" />
                            Комментарий
                          </Button>
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {planData.instances.length === 0 && (
            <Card className="p-8 text-center">
              <Calendar className="w-12 h-12 mx-auto mb-4 text-tg-hint" />
              <p className="text-tg-hint">Нет задач на выбранный период</p>
            </Card>
          )}
        </div>
      )}

      {/* Модальное окно для работы с задачей */}
      {selectedInstance && (
        <ChecklistInstanceModal
          instance={selectedInstance}
          onClose={() => setSelectedInstance(null)}
        />
      )}
    </div>
  );
}

